import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { getUserDetails, deleteUserAccount } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import "./UserDetails.css";

interface Feedback {
  id: number;
  rating: number;
  comment: string;
  sender_username: string;
  recipient_username: string;
  product_id: number;
}

interface Product {
  id: number;
  name: string;
  price: number;
  description: string;
  category: string;
  is_sold: boolean;
  created_at: string;
}

interface Transaction {
  id: number;
  status: string;
  created_at: string;
  buyer_id: number;
  seller_id: number;
  product_id: number;
}

interface UserDetailsData {
  id: number;
  username: string;
  email: string;
  access_level: string;
  phone_number: string;
  profile_picture: string | null;
  feedbacks_sent: Feedback[];
  feedbacks_received: Feedback[];
  products: Product[];
  transactions: Transaction[];
}

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userData, setUserData] = useState<UserDetailsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await getUserDetails(Number(id));
        setUserData(response.data);
      } catch (error) {
        console.error("Failed to load user details", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) loadUser();
  }, [id]);

  const handleDeleteUser = async () => {
    try {
      await deleteUserAccount(Number(id));
      setShowDeleteModal(false);
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      console.error(error);
      alert("Failed to delete user.");
    }
  };
  

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </>
    );
  }

  if (!userData) {
    return (
      <>
        <Navbar />
        <div className="container">
          <div className="error">User not found</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="user-details-container">
        <div className="container">
          <h1>User Details: {userData.username}</h1>

          <div className="user-info">
          {userData.profile_picture && (
  <img
    src={userData.profile_picture || "/default-profile.png"}
    alt="Profile"
    className="profile-img"
  />
)}

  <label>
    <strong>Email:</strong>
    <input
      type="email"
      value={userData.email}
      onChange={(e) => setUserData({ ...userData, email: e.target.value })}
    />
  </label>

  <label>
    <strong>Phone:</strong>
    <input
      type="text"
      value={userData.phone_number || ""}
      onChange={(e) => setUserData({ ...userData, phone_number: e.target.value })}
    />
  </label>

  <label>
    <strong>Access Level:</strong>
    <select
      value={userData.access_level}
      onChange={(e) =>
        setUserData({ ...userData, access_level: e.target.value })
      }
    >
      <option value="buyer">Buyer</option>
      <option value="seller">Seller</option>
      <option value="admin">Admin</option>
    </select>
  </label>

  

  <div className="user-actions">
    <button className="save-user-btn" onClick={async () => {
      try {
        await api.put(`/admin/users/${userData.id}`, {
          email: userData.email,
          phone_number: userData.phone_number,
          access_level: userData.access_level
        });
        setShowSuccessModal(true);    
         } catch (error) {
        console.error("Failed to update user", error);
        alert("Update failed");
      }
    }}>
      Save Changes
    </button>

    <button className="delete-user-btn" onClick={() => setShowDeleteModal(true)}>
  Delete User
</button>

  </div>
</div>

<hr className="section-divider" />
          <h2>Products</h2>
          {userData.products.length === 0 ? (
            <p>No products found</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Category</th>
                  <th>Sold</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {userData.products.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.price}</td>
                    <td>{p.category}</td>
                    <td>{p.is_sold ? "Yes" : "No"}</td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <Link to={`/product/${p.id}`} className="btn-view-product">
                        View
                    </Link>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

<hr className="section-divider" />
          <h2>Feedbacks</h2>
          {userData.feedbacks_sent.length + userData.feedbacks_received.length === 0 ? (
            <p>No feedbacks available</p>
          ) : (
            <div className="feedback-lists">
              <div>
                <h3>Sent Feedbacks</h3>
                {userData.feedbacks_sent.map((f) => (
                  <div key={f.id} className="feedback-card">
                    <p><strong>To:</strong> {f.recipient_username || "Unknown"}</p>
                    <p><strong>Rating:</strong> {f.rating}</p>
                    <p>{f.comment}</p>
                  </div>
                ))}
              </div>

              <div>
                <h3>Received Feedbacks</h3>
                {userData.feedbacks_received.map((f) => (
                  <div key={f.id} className="feedback-card">
                    <p><strong>From:</strong> {f.sender_username || "Unknown"}</p>
                    <p><strong>Rating:</strong> {f.rating}</p>
                    <p>{f.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

<hr className="section-divider" />
          <h2>Transactions</h2>
          {userData.transactions.length === 0 ? (
            <p>No transactions found</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product ID</th>
                  <th>Buyer ID</th>
                  <th>Seller ID</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {userData.transactions.map((t) => (
                  <tr key={t.id}>
                    <td>{t.id}</td>
                    <td>{t.product_id}</td>
                    <td>{t.buyer_id}</td>
                    <td>{t.seller_id}</td>
                    <td>{t.status}</td>
                    <td>{new Date(t.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {showSuccessModal && (
  <div className="modal-overlay">
    <div className="success-modal">
      <div className="success-icon">✓</div>
      <h3>User updated successfully!</h3>
      <button
        onClick={() => { setShowSuccessModal(false); }}
        className="return-btn"
      >
        Return
      </button>
    </div>
  </div>
)}
{showDeleteModal && (
  <div className="modal-overlay">
    <div className="confirm-modal">
      <div className="warning-icon">!</div>
      <h3>Are you sure you want to delete this user?</h3>
      <div className="confirm-actions">
        <button onClick={handleDeleteUser} className="confirm-delete-btn">
          Delete
        </button>
        <button onClick={() => setShowDeleteModal(false)} className="cancel-delete-btn">
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

    </>
  );
};

export default UserDetails;
